import axios from 'axios'
import { logPageApiCall, logPageApiError, logPageApiResult, shouldLogMuteluApiDebug } from '../utils/apiDebugLogger'

const API_BASE_URL = process.env.REACT_APP_BIGC_BIGLINE_API_URL
const API_KEY = process.env.REACT_APP_BIGC_BIGLINE_API_KEY || process.env.REACT_APP_API_KEY

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000
})

const responseCache = new Map()
const inflightRequests = new Map()
let initPlayerInFlight = null
let initPlayerLastSuccessAt = 0
const INIT_PLAYER_CACHE_MS = 120000
let pendingResolveInFlight = null
let pendingResolveLastRunAt = 0
const PENDING_RESOLVE_CACHE_MS = 8000
const TICKET_BALANCE_SNAPSHOT_KEY = 'mutelu:ticket-balance:snapshot'
const PARTY_OWNED_SNAPSHOT_KEY = 'mutelu:party-owned:snapshot'
const PARTY_JOINED_SNAPSHOT_KEY = 'mutelu:party-joined:snapshot'
const FORCE_TICKET_REFRESH_KEY = 'mutelu:force-ticket-refresh'
const FORCE_PARTY_REFRESH_KEY = 'mutelu:force-party-refresh'

const readCache = (key) => {
  const cached = responseCache.get(key)
  if (!cached) return null
  if (cached.expiresAt <= Date.now()) {
    responseCache.delete(key)
    return null
  }
  return cached.value
}

const writeCache = (key, value, ttlMs) => {
  if (!ttlMs || ttlMs <= 0) return
  responseCache.set(key, {
    value,
    expiresAt: Date.now() + ttlMs
  })
}

const clearCacheByPrefix = (prefix) => {
  const keys = Array.from(responseCache.keys())
  keys.forEach((key) => {
    if (String(key).startsWith(prefix)) {
      responseCache.delete(key)
    }
  })
}

const persistTicketBalanceSnapshot = (balance) => {
  if (!balance || typeof balance !== 'object') return
  localStorage.setItem(TICKET_BALANCE_SNAPSHOT_KEY, JSON.stringify(balance))
  localStorage.setItem(FORCE_TICKET_REFRESH_KEY, String(Date.now()))
}

const persistOwnedPartySnapshot = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return
  localStorage.setItem(PARTY_OWNED_SNAPSHOT_KEY, JSON.stringify(snapshot))
  localStorage.setItem(FORCE_PARTY_REFRESH_KEY, String(Date.now()))
}

const persistJoinedPartySnapshot = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return
  localStorage.setItem(PARTY_JOINED_SNAPSHOT_KEY, JSON.stringify(snapshot))
  localStorage.setItem(FORCE_PARTY_REFRESH_KEY, String(Date.now()))
}

const requestWithCache = async (key, requestFn, ttlMs = 0) => {
  if (!key || ttlMs <= 0) {
    return requestFn()
  }

  const cached = readCache(key)
  if (cached != null) return cached

  if (inflightRequests.has(key)) {
    return inflightRequests.get(key)
  }

  const requestPromise = Promise.resolve()
    .then(() => requestFn())
    .then((result) => {
      writeCache(key, result, ttlMs)
      return result
    })
    .finally(() => {
      inflightRequests.delete(key)
    })

  inflightRequests.set(key, requestPromise)
  return requestPromise
}

api.interceptors.request.use((config) => {
  const nextConfig = { ...config }
  const headers = { ...(nextConfig.headers || {}) }

  const lineMid = localStorage.getItem('userId')
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  const allowDebugLineMid = isLocalhost && localStorage.getItem('mutelu_enable_debug_line_mid') === '1'
  const debugLineMid = localStorage.getItem('mutelu_debug_line_mid') || 'U_LOCAL_MUTELU_TICKET_001'
  const authToken = localStorage.getItem('authToken')
  const lineDisplayName = localStorage.getItem('line_display_name') || ''
  const clientAuth = process.env.REACT_APP_CLIENT_AUTH
  const language = (localStorage.getItem('i18nextLng') || 'TH').toUpperCase()

  if (lineMid) headers['x-line-mid'] = lineMid
  if (!lineMid && allowDebugLineMid) {
    headers['x-line-mid'] = debugLineMid
    console.log('[muteluApi] Using debug x-line-mid for localhost (opt-in):', debugLineMid)
  }
  if (lineDisplayName.trim()) {
    headers['x-line-display-name'] = encodeURIComponent(lineDisplayName.trim())
  }
  if (authToken) headers.Authorization = `Bearer ${authToken}`
  if (clientAuth) headers['X-Client-Auth'] = clientAuth
  if (API_KEY) headers['x-api-key'] = API_KEY
  headers['Accept-Language'] = language

  nextConfig.headers = headers

  if (shouldLogMuteluApiDebug()) {
    logPageApiCall('muteluApi', `${String(nextConfig.method || 'get').toUpperCase()} ${nextConfig.url}`, {
      params: nextConfig.params || null,
      data: nextConfig.data || null
    })
  }

  return nextConfig
})

api.interceptors.response.use(
  (response) => {
    if (shouldLogMuteluApiDebug()) {
      logPageApiResult(
        'muteluApi',
        `${String(response?.config?.method || 'get').toUpperCase()} ${response?.config?.url || ''}`,
        {
          status: response?.status,
          data: response?.data || null
        }
      )
    }
    return response.data
  },
  (error) => {
    if (shouldLogMuteluApiDebug()) {
      logPageApiError(
        'muteluApi',
        `${String(error?.config?.method || 'get').toUpperCase()} ${error?.config?.url || ''}`,
        error
      )
    }

    if (error?.response) {
      const payload = error.response.data
      const wrappedError = new Error(
        payload?.message || payload?.error || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
      )
      wrappedError.response = error.response
      wrappedError.status = error.response.status
      wrappedError.data = payload
      return Promise.reject(wrappedError)
    }

    if (error?.request) {
      const networkError = new Error('ไม่สามารถเชื่อมต่อได้ กรุณาตรวจสอบอินเทอร์เน็ต')
      networkError.isNetworkError = true
      return Promise.reject(networkError)
    }

    return Promise.reject(error)
  }
)

export const muteluApi = {
  initPlayer: async () =>
    api.post('/game-bigpoint/init', {
      lineId: localStorage.getItem('userId') || null,
      displayName: localStorage.getItem('line_display_name') || null,
      pictureUrl: localStorage.getItem('line_picture_url') || null,
      bigpointId: localStorage.getItem('bigCardNumber') || null
    }),
  initPlayerOnce: async (force = false) => {
    const now = Date.now()
    if (!force && initPlayerLastSuccessAt > 0 && now - initPlayerLastSuccessAt < INIT_PLAYER_CACHE_MS) {
      muteluApi.resolvePendingPartyJoin().catch(() => {})
      return {
        success: true,
        cached: true
      }
    }

    if (!force && initPlayerInFlight) {
      return initPlayerInFlight
    }

    const requestPromise = muteluApi
      .initPlayer()
      .then((result) => {
        initPlayerLastSuccessAt = Date.now()
        muteluApi.resolvePendingPartyJoin(true).catch(() => {})
        return result
      })
      .finally(() => {
        if (initPlayerInFlight === requestPromise) {
          initPlayerInFlight = null
        }
      })

    initPlayerInFlight = requestPromise
    return requestPromise
  },
  resolvePendingPartyJoin: async (force = false) => {
    const now = Date.now()
    if (!force && pendingResolveLastRunAt > 0 && now - pendingResolveLastRunAt < PENDING_RESOLVE_CACHE_MS) {
      return {
        success: true,
        cached: true,
        data: null
      }
    }

    if (!force && pendingResolveInFlight) {
      return pendingResolveInFlight
    }

    const requestPromise = api
      .post('/game-mutelu/party/pending/resolve')
      .then((result) => {
        pendingResolveLastRunAt = Date.now()
        if (result?.data?.resolved) {
          clearCacheByPrefix('mutelu:party:')
          clearCacheByPrefix('mutelu:tickets:')
          clearCacheByPrefix('mutelu:merit:')
        }
        return result
      })
      .finally(() => {
        if (pendingResolveInFlight === requestPromise) {
          pendingResolveInFlight = null
        }
      })

    pendingResolveInFlight = requestPromise
    return requestPromise
  },
  submitReceipt: async (payload) => {
    const response = await api.post('/game-mutelu/receipt/submit', payload)
    clearCacheByPrefix('mutelu:tickets:')
    return response
  },
  getFortuneByReceiptId: async (receiptId) => api.get(`/game-mutelu/fortune/${receiptId}`),
  getLatestReceiptResult: async () =>
    requestWithCache('mutelu:receipt:latest', () => api.get('/game-mutelu/receipt/latest'), 5000),
  getTicketBalance: async (force = false) => {
    if (force) {
      clearCacheByPrefix('mutelu:tickets:')
      return api.get('/game-mutelu/tickets/balance')
    }
    return requestWithCache('mutelu:tickets:balance', () => api.get('/game-mutelu/tickets/balance'), 12000)
  },
  getMeritRooms: async (force = false) => {
    if (force) {
      clearCacheByPrefix('mutelu:merit:')
      return api.get('/game-mutelu/merit/rooms')
    }
    return requestWithCache('mutelu:merit:rooms', () => api.get('/game-mutelu/merit/rooms'), 8000)
  },
  getMeritRoomStatus: async (roomId) =>
    requestWithCache(
      `mutelu:merit:room-status:${encodeURIComponent(roomId)}`,
      () => api.get(`/game-mutelu/merit/rooms/${encodeURIComponent(roomId)}/status`),
      5000
    ),
  pourMeritRoom: async (roomId, payload = {}) => {
    const response = await api.post(`/game-mutelu/merit/rooms/${encodeURIComponent(roomId)}/pour`, payload)
    clearCacheByPrefix('mutelu:merit:')
    clearCacheByPrefix('mutelu:tickets:')
    return response
  },
  getMeritCalendar: async (roomId, month) =>
    requestWithCache(
      `mutelu:merit:calendar:${encodeURIComponent(roomId)}:${month || 'current'}`,
      () =>
        api.get(`/game-mutelu/merit/rooms/${encodeURIComponent(roomId)}/calendar`, {
          params: month ? { month } : undefined
        }),
      5000
    ),
  createParty: async () => {
    const response = await api.post('/game-mutelu/party/create')
    clearCacheByPrefix('mutelu:party:')
    clearCacheByPrefix('mutelu:tickets:')
    clearCacheByPrefix('mutelu:merit:')
    persistOwnedPartySnapshot(response?.data || response)
    persistTicketBalanceSnapshot(response?.data?.ticket_balance || response?.ticket_balance)
    return response
  },
  joinParty: async (groupCode) => {
    const response = await api.post('/game-mutelu/party/join', { group_code: groupCode })
    clearCacheByPrefix('mutelu:party:')
    clearCacheByPrefix('mutelu:tickets:')
    clearCacheByPrefix('mutelu:merit:')
    if (!(response?.needs_registration || response?.data?.needs_registration)) {
      persistJoinedPartySnapshot(response?.data || response)
      persistTicketBalanceSnapshot(response?.data?.ticket_balance || response?.ticket_balance)
    }
    return response
  },
  getPartyStatus: async (groupCode) =>
    requestWithCache(
      `mutelu:party:status:${encodeURIComponent(groupCode)}`,
      () => api.get(`/game-mutelu/party/${encodeURIComponent(groupCode)}/status`),
      5000
    ),
  getPartyMembers: async (groupCode) =>
    requestWithCache(
      `mutelu:party:members:${encodeURIComponent(groupCode)}`,
      () => api.get(`/game-mutelu/party/${encodeURIComponent(groupCode)}/members`),
      5000
    ),
  getMyOwnedParty: async (force = false) => {
    if (force) {
      clearCacheByPrefix('mutelu:party:')
      return api.get('/game-mutelu/party/my-owned')
    }
    return requestWithCache('mutelu:party:my-owned', () => api.get('/game-mutelu/party/my-owned'), 7000)
  },
  getMyJoinedParties: async (force = false) => {
    if (force) {
      clearCacheByPrefix('mutelu:party:')
      return api.get('/game-mutelu/party/my-joined')
    }
    return requestWithCache('mutelu:party:my-joined', () => api.get('/game-mutelu/party/my-joined'), 7000)
  },
  getPartySummary: async () =>
    requestWithCache('mutelu:party:summary', () => api.get('/game-mutelu/party/summary'), 5000),
  getDashboardLeaderboard: async (limit = 20, force = false) => {
    const normalizedLimit = Number.isFinite(Number(limit)) ? Number(limit) : 20
    const cacheKey = `mutelu:dashboard:leaderboard:${normalizedLimit}`
    if (force) {
      clearCacheByPrefix('mutelu:dashboard:')
      return api.get('/game-mutelu/dashboard/leaderboard', {
        params: { limit: normalizedLimit }
      })
    }
    return requestWithCache(
      cacheKey,
      () =>
        api.get('/game-mutelu/dashboard/leaderboard', {
          params: { limit: normalizedLimit }
        }),
      10000
    )
  }
}

export default muteluApi
